<template>
  <el-dialog
    :title="saved ? '新增成功' : '添加设备'"
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    width="880px"
    :close-on-click-modal="false"
    @close="resetForm"
  >
    <el-form :model="form" :rules="rules" ref="form" label-width="100px" class="equipment-form">
      <!-- 基础信息（两列） -->
      <div class="section-title">基础信息</div>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="设备名称" prop="name" :class="{ 'ocr-filled': ocrFilled.name }">
            <el-input v-model="form.name" placeholder="请输入设备名称" @input="ocrFilled.name = false" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="规格型号" prop="model" :class="{ 'ocr-filled': ocrFilled.model }">
            <el-input v-model="form.model" placeholder="请输入规格型号" @input="ocrFilled.model = false" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="出厂编号" prop="factory_no" :class="{ 'ocr-filled': ocrFilled.factory_no }">
            <el-input
              v-model="form.factory_no"
              placeholder="请输入出厂编号"
              :loading="checking"
              @blur="checkFactoryNo"
              @input="onFactoryNoInput"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="使用科室" prop="department">
            <department-select v-model="form.department" placeholder="请输入使用科室" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="生产厂家" prop="manufacturer" :class="{ 'ocr-filled': ocrFilled.manufacturer }">
            <el-input v-model="form.manufacturer" placeholder="请输入生产厂家" @input="ocrFilled.manufacturer = false" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="安装日期">
            <el-date-picker v-model="form.install_date" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="生产日期" :class="{ 'ocr-filled': ocrFilled.production_date }">
            <el-date-picker v-model="form.production_date" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" style="width: 100%" @change="ocrFilled.production_date = false" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="使用期限" :class="{ 'ocr-filled': ocrFilled.service_life }">
            <el-input v-model="form.service_life" placeholder="请输入使用期限" @input="ocrFilled.service_life = false">
              <template slot="suffix">年</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="设备价格">
            <el-input v-model="form.price" placeholder="价格（元）" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="到期日期">
            <el-date-picker v-model="form.warranty_expiry_date" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="使用状态">
            <el-select v-model="form.status" placeholder="请选择" style="width: 100%">
              <el-option label="在用" :value="1" />
              <el-option label="报废" :value="2" />
              <el-option label="维修" :value="3" />
              <el-option label="停用" :value="4" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="固定资产">
            <el-select v-model="form.is_fixed_asset" placeholder="请选择" style="width: 100%">
              <el-option label="是" value="是" />
              <el-option label="否" value="否" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- 图片与二维码 -->
      <div class="section-title">图片与二维码</div>
      <el-row :gutter="24" class="upload-row">
        <el-col :span="8">
          <div class="upload-label">设备照片</div>
          <el-upload
            ref="photoUpload"
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handlePhotoChange"
            accept="image/*"
            drag
            class="image-uploader"
          >
            <div class="image-box" :class="{ 'has-image': photoPreviewUrl }">
              <img v-if="photoPreviewUrl" :src="photoPreviewUrl" class="preview-img" alt="设备照片" />
              <div v-else class="upload-placeholder">
                <i class="el-icon-plus upload-icon"></i>
                <div class="upload-tip">点击或拖拽上传设备照片</div>
              </div>
              <div v-if="photoPreviewUrl" class="image-mask">
                <i class="el-icon-zoom-in mask-icon" title="预览大图" @click.stop="handlePhotoPreview"></i>
                <i class="el-icon-delete mask-icon" title="删除" @click.stop="handlePhotoRemove"></i>
              </div>
            </div>
          </el-upload>
        </el-col>

        <el-col :span="8">
          <div class="upload-label">设备铭牌</div>
          <el-upload
            ref="nameplateUpload"
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleNameplateChange"
            accept="image/*"
            drag
            class="image-uploader"
          >
            <div class="image-box" :class="{ 'has-image': nameplatePreviewUrl }">
              <img v-if="nameplatePreviewUrl" :src="nameplatePreviewUrl" class="preview-img" alt="设备铭牌" />
              <div v-else class="upload-placeholder">
                <i class="el-icon-plus upload-icon"></i>
                <div class="upload-tip">点击或拖拽上传设备铭牌</div>
              </div>
              <div v-if="nameplatePreviewUrl" class="image-mask">
                <i class="el-icon-zoom-in mask-icon" title="预览大图" @click.stop="handleNameplatePreview"></i>
                <i class="el-icon-delete mask-icon" title="删除" @click.stop="handleNameplateRemove"></i>
              </div>
            </div>
          </el-upload>
          <div style="text-align: center; margin-top: 8px">
            <el-button
              size="small"
              type="success"
              icon="el-icon-magic-stick"
              :disabled="!nameplateFile"
              :loading="recognizing"
              @click="handleNameplateOcr"
            >识别铭牌回填</el-button>
          </div>
          <div class="ocr-tip">上传铭牌照片后点击识别，自动回填可识别字段</div>
        </el-col>

        <el-col :span="8">
          <div class="upload-label">二维码</div>
          <div class="image-box" :class="{ 'has-image': qrDisplayUrl }">
            <img v-if="qrDisplayUrl" :src="qrDisplayUrl" class="preview-img" alt="二维码" />
            <div v-else class="upload-placeholder">
              <el-button type="primary" size="small" icon="el-icon-full-screen" @click="generateQrPreview">
                生成二维码
              </el-button>
              <div class="upload-tip" style="margin-top: 8px">保存后自动持久化</div>
            </div>
          </div>
          <div v-if="saved && qrcodeUrl" class="qr-success">
            <p>已生成，扫码可报修/查看</p>
          </div>
        </el-col>
      </el-row>

      <!-- 设备资质 -->
      <div class="section-title">设备资质</div>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="合格证" label-width="90px">
            <el-select v-model="form.has_certificate" placeholder="请选择" style="width: 100%">
              <el-option label="有" value="有" />
              <el-option label="无" value="无" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="注册证" label-width="90px">
            <el-select v-model="form.has_registration" placeholder="请选择" style="width: 100%">
              <el-option label="有" value="有" />
              <el-option label="无" value="无" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="生产许可证" label-width="90px">
            <el-select v-model="form.has_production_license" placeholder="请选择" style="width: 100%">
              <el-option label="有" value="有" />
              <el-option label="无" value="无" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="营业执照" label-width="90px">
            <el-select v-model="form.has_business_license" placeholder="请选择" style="width: 100%">
              <el-option label="有" value="有" />
              <el-option label="无" value="无" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="有效期至" label-width="90px">
            <el-date-picker v-model="form.expire_date" type="date" value-format="yyyy-MM-dd" placeholder="选择有效期" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="资质文件" label-width="90px">
            <el-upload
              action="#"
              :auto-upload="false"
              :on-change="handleQualFileChange"
              :on-remove="handleQualFileRemove"
              :file-list="qualFileList"
              accept=".pdf"
            >
              <el-button type="primary" icon="el-icon-upload">点击上传 PDF</el-button>
              <div slot="tip" class="el-upload__tip">仅支持 PDF 文件，大小不超过 10MB</div>
            </el-upload>
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item label="备注信息">
            <el-input v-model="form.remark" type="textarea" :rows="3" placeholder="请输入备注" />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <span slot="footer">
      <template v-if="saved">
        <el-button type="primary" @click="closeDialog">完成</el-button>
      </template>
      <template v-else>
        <el-button @click="resetAndClose">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">保存</el-button>
      </template>
    </span>

    <!-- 图片放大预览：自绘悬浮窗（可拖到屏幕任意处、点击窗外穿透可直接填表；滚轮缩放 + 拖拽平移） -->
    <div
      v-if="previewVisible"
      class="preview-float"
      :style="{ left: panelX + 'px', top: panelY + 'px', width: panelW + 'px' }"
    >
      <div class="preview-float-header" @mousedown.prevent="onPanelDragStart">
        <span class="preview-float-title">{{ previewTitle }}</span>
        <i class="el-icon-close preview-float-close" title="关闭" @click="closePreview"></i>
      </div>
      <div class="preview-float-body" @wheel.prevent="onPreviewWheel">
        <img
          v-if="previewUrl"
          :src="previewUrl"
          :style="previewImgStyle"
          alt="预览"
          @mousedown.prevent="onPreviewMouseDown"
          @mousemove="onPreviewMouseMove"
          @mouseup="onPreviewMouseUp"
          @mouseleave="onPreviewMouseUp"
          @dblclick="resetPreviewTransform"
        />
      </div>
      <div class="preview-float-tip">拖动标题栏移动窗体 · 滚轮缩放 · 按住图片平移 · 双击复位 · Esc 关闭</div>
    </div>
  </el-dialog>
</template>

<script>
import equipmentApi from '../api/equipment'
import QRCode from 'qrcode'

import DepartmentSelect from './DepartmentSelect.vue'

export default {
  name: 'EquipmentForm',
  components: { DepartmentSelect },
  props: {
    visible: Boolean,
  },
  data() {
    return {
      form: this.emptyForm(),
      rules: {
        name: [{ required: true, message: '请输入设备名称', trigger: 'blur' }],
        factory_no: [
          { required: true, message: '请输入出厂编号', trigger: 'blur' },
          { validator: this.validateFactoryNo, trigger: 'blur' },
        ],
        service_life: [
          { pattern: /^\d+$/, message: '使用期限请输入正整数（单位：年）', trigger: 'blur' },
        ],
        price: [
          { pattern: /^\d+(\.\d{1,2})?$/, message: '价格格式不正确（最多两位小数）', trigger: 'blur' },
        ],
      },

      // 出厂编号查重状态
      checking: false,
      factoryNoExists: false,

      photoFile: null,
      nameplateFile: null,
      qualFileList: [],
      qualFile: null,

      photoPreviewUrl: '',
      nameplatePreviewUrl: '',
      previewVisible: false,
      previewUrl: '',
      previewTitle: '',
      previewScale: 1,
      previewTx: 0,
      previewTy: 0,
      previewDragging: false,
      panelX: null,
      panelY: null,
      panelW: 0,
      recognizing: false,
      ocrFilled: {
        name: false,
        model: false,
        factory_no: false,
        manufacturer: false,
        production_date: false,
        service_life: false,
      },
      qrPreviewUrl: '',
      qrcodeUrl: '',
      saved: false,
      submitting: false,
    }
  },
  computed: {
    qrDisplayUrl() {
      // 保存成功后有后端返回的真实二维码；保存前可本地预览
      return this.qrcodeUrl || this.qrPreviewUrl || ''
    },
    previewImgStyle() {
      return {
        maxWidth: '100%',
        maxHeight: '100%',
        transform: `translate(${this.previewTx}px, ${this.previewTy}px) scale(${this.previewScale})`,
        transformOrigin: 'center center',
        cursor: this.previewDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }
    },
  },
  watch: {
    visible(val) {
      if (val) {
        // 每次打开都重置为空白表单
        this.form = this.emptyForm()
        this.resetFiles()
        this.qrcodeUrl = ''
        this.qrPreviewUrl = ''
      this.saved = false
        this.factoryNoExists = false
        this.checking = false
        this.clearOcrFilled()
        this.closePreview()
      }
    },
    // 生产日期或使用期限变化，自动推算到期日期
    'form.production_date': 'calcExpiryDate',
    'form.service_life': 'calcExpiryDate',
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        model: '',
        factory_no: '',
        department: '',
        manufacturer: '',
        production_date: '',
        install_date: '',
        warranty_expiry_date: '',
        service_life: '',
        price: '',
        status: 1,
        is_fixed_asset: '是',
        // 4 个资质“有/无”，默认“有”
        has_certificate: '有',
        has_registration: '有',
        has_production_license: '有',
        has_business_license: '有',
        expire_date: '',
        remark: '',
      }
    },
    revokeBlob(url) {
      if (url && url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url)
      }
    },
    resetFiles() {
      this.revokeBlob(this.photoPreviewUrl)
      this.revokeBlob(this.nameplatePreviewUrl)
      this.photoPreviewUrl = ''
      this.nameplatePreviewUrl = ''
      this.qrPreviewUrl = ''
      this.photoFile = null
      this.nameplateFile = null
      this.qualFileList = []
      this.qualFile = null
      if (this.$refs.photoUpload) this.$refs.photoUpload.clearFiles()
      if (this.$refs.nameplateUpload) this.$refs.nameplateUpload.clearFiles()
    },
    // 出厂编号失焦查重：调用列表接口按 factory_no 过滤，命中即提示已存在
    async checkFactoryNo() {
      const val = (this.form.factory_no || '').trim()
      // 清空或之前已标记存在则重置，等待重新校验
      this.factoryNoExists = false
      if (!val) return
      this.checking = true
      try {
        const res = await equipmentApi.list({ factory_no: val, page: 1, page_size: 1 })
        const exists = res.data && res.data.results && res.data.results.length > 0
        this.factoryNoExists = exists
        if (exists) {
          // 触发本字段校验，让错误提示渲染出来
          this.$refs.form.validateField('factory_no')
        }
      } catch (e) {
        // 查重失败（如后端未启动）不阻断填写，仅忽略
        this.factoryNoExists = false
      } finally {
        this.checking = false
      }
    },
    // 出厂编号输入变化时重置查重标记，避免旧提示残留
    onFactoryNoInput() {
      this.factoryNoExists = false
      this.ocrFilled.factory_no = false
    },
    // 铭牌 OCR 识别回填：把识别到的字段填入表单并标黄提示核对
    async handleNameplateOcr() {
      if (!this.nameplateFile) {
        this.$message.warning('请先上传铭牌照片')
        return
      }
      this.recognizing = true
      try {
        const fd = new FormData()
        fd.append('image_file', this.nameplateFile)
        const res = await equipmentApi.nameplateExtract(fd)
        const data = (res.data && res.data.data) || {}
        const hint = (res.data && res.data.hint) || ''
        const fields = ['name', 'model', 'factory_no', 'manufacturer', 'production_date', 'service_life']
        let filled = 0
        fields.forEach((k) => {
          const v = data[k]
          if (v === null || v === undefined || v === '') return
          this.form[k] = String(v)
          this.ocrFilled[k] = true
          filled++
        })
        if (filled > 0) {
          this.$message.success(`识别完成，已回填 ${filled} 项（黄色底色字段），请核对无误后再保存`)
        } else {
          this.$message.warning(hint || '未识别到可回填的字段，请手动填写')
        }
      } catch (e) {
        const resp = e.response && e.response.data
        const detail = resp && (resp.detail || resp.hint)
        this.$message.error(detail || '铭牌识别失败，请重试')
      } finally {
        this.recognizing = false
      }
    },
    clearOcrFilled() {
      Object.keys(this.ocrFilled).forEach((k) => {
        this.ocrFilled[k] = false
      })
    },
    // 自动生成到期日期：生产日期年份 + 使用期限（年），月日不变
    calcExpiryDate() {
      const pd = this.form.production_date
      const life = parseInt(this.form.service_life, 10)
      if (pd && !isNaN(life) && life > 0) {
        const parts = pd.split('-')
        if (parts.length === 3) {
          const y = parseInt(parts[0], 10)
          const m = parts[1]
          const d = parts[2]
          const ny = y + life
          this.form.warranty_expiry_date = `${ny}-${m}-${d}`
        }
      }
    },
    // 接入 el-form 校验：查重命中则报“已存在”，否则放行
    validateFactoryNo(rule, value, callback) {
      if (this.factoryNoExists) {
        callback(new Error('该出厂编号已存在，请更换'))
      } else {
        callback()
      }
    },
    // 拖拽可能带进 PDF/Word 等非图片文件：accept 只约束"点击选择"的对话框，
    // 管不住拖拽，必须在这里显式拦（否则会拿 createObjectURL 生成一个打不开的破图）
    isImageFile(file) {
      const raw = file && file.raw
      return !!raw && /^image\//.test(raw.type || '')
    },
    handlePhotoChange(file) {
      if (!this.isImageFile(file)) {
        this.$message.warning('只能上传图片文件（jpg / png 等）')
        return
      }
      this.revokeBlob(this.photoPreviewUrl)
      this.photoFile = file.raw
      this.photoPreviewUrl = window.URL.createObjectURL(file.raw)
    },
    handlePhotoRemove() {
      this.revokeBlob(this.photoPreviewUrl)
      this.photoFile = null
      this.photoPreviewUrl = ''
      if (this.$refs.photoUpload) this.$refs.photoUpload.clearFiles()
    },
    handlePhotoPreview() {
      this.openPreview(this.photoPreviewUrl, '设备照片预览')
    },
    handleNameplateChange(file) {
      if (!this.isImageFile(file)) {
        this.$message.warning('只能上传图片文件（jpg / png 等）')
        return
      }
      this.revokeBlob(this.nameplatePreviewUrl)
      this.nameplateFile = file.raw
      this.nameplatePreviewUrl = window.URL.createObjectURL(file.raw)
    },
    handleNameplateRemove() {
      this.revokeBlob(this.nameplatePreviewUrl)
      this.nameplateFile = null
      this.nameplatePreviewUrl = ''
      if (this.$refs.nameplateUpload) this.$refs.nameplateUpload.clearFiles()
    },
    handleNameplatePreview() {
      this.openPreview(this.nameplatePreviewUrl, '设备铭牌预览')
    },
    // 预览图：滚轮缩放 + 拖拽平移
    onPreviewWheel(e) {
      const factor = e.deltaY > 0 ? 0.9 : 1.1
      let next = this.previewScale * factor
      next = Math.min(4, Math.max(0.5, next))
      this.previewScale = next
    },
    onPreviewMouseDown(e) {
      this.previewDragging = true
      this._px = e.clientX
      this._py = e.clientY
    },
    onPreviewMouseMove(e) {
      if (!this.previewDragging) return
      this.previewTx += e.clientX - this._px
      this.previewTy += e.clientY - this._py
      this._px = e.clientX
      this._py = e.clientY
    },
    onPreviewMouseUp() {
      this.previewDragging = false
    },
    resetPreviewTransform() {
      this.previewScale = 1
      this.previewTx = 0
      this.previewTy = 0
      this.previewDragging = false
    },
    // ---- 自绘预览悬浮窗：打开 / 关闭 / 拖动 ----
    openPreview(url, title) {
      this.previewUrl = url
      this.previewTitle = title
      this.resetPreviewTransform()
      if (!this.panelW) {
        this.panelW = Math.min(920, Math.max(420, Math.round(window.innerWidth * 0.55)))
      }
      if (this.panelX === null || this.panelY === null) {
        // 默认放屏幕右侧，避开居中的表单；用户拖过就保留上次位置
        this.panelX = Math.max(20, window.innerWidth - this.panelW - 48)
        this.panelY = 90
      }
      this.panelX = Math.min(Math.max(this.panelX, 40 - this.panelW), window.innerWidth - 80)
      this.panelY = Math.min(Math.max(this.panelY, 0), window.innerHeight - 80)
      this.previewVisible = true
      document.addEventListener('keydown', this.onPreviewKeydown)
    },
    closePreview() {
      this.previewVisible = false
      this.resetPreviewTransform()
      document.removeEventListener('keydown', this.onPreviewKeydown)
    },
    onPreviewKeydown(e) {
      if (e.key === 'Escape') this.closePreview()
    },
    onPanelDragStart(e) {
      const offX = e.clientX - this.panelX
      const offY = e.clientY - this.panelY
      const move = (ev) => {
        this.panelX = Math.min(Math.max(ev.clientX - offX, 40 - this.panelW), window.innerWidth - 80)
        this.panelY = Math.min(Math.max(ev.clientY - offY, 0), window.innerHeight - 60)
      }
      const up = () => {
        document.removeEventListener('mousemove', move)
        document.removeEventListener('mouseup', up)
        window.removeEventListener('blur', up)
      }
      document.addEventListener('mousemove', move)
      document.addEventListener('mouseup', up)
      window.addEventListener('blur', up) // 拖出浏览器窗口松开鼠标时也能结束拖拽
    },
    handleQualFileChange(file, fileList) {
      this.qualFileList = fileList.slice(-1)
      this.qualFile = file.raw
    },
    handleQualFileRemove() {
      this.qualFileList = []
      this.qualFile = null
    },
    async generateQrPreview() {
      if (!this.form.name && !this.form.factory_no) {
        this.$message.warning('请至少填写设备名称或出厂编号后再生成二维码')
        return
      }
      const content = `https://your-domain.com/h5/equipment/PREVIEW?n=${encodeURIComponent(
        this.form.name || ''
      )}&f=${encodeURIComponent(this.form.factory_no || '')}&t=${Date.now()}`
      try {
        this.qrPreviewUrl = await QRCode.toDataURL(content, { width: 160, margin: 2 })
      } catch (err) {
        this.$message.error('二维码生成失败：' + err.message)
      }
    },
    resetForm() {
      if (this.$refs.form) this.$refs.form.resetFields()
      this.resetFiles()
      this.qrcodeUrl = ''
      this.qrPreviewUrl = ''
      this.saved = false
    },
    resetAndClose() {
      this.$emit('update:visible', false)
    },
    closeDialog() {
      this.$emit('update:visible', false)
    },
    async handleSubmit() {
      const valid = await this.$refs.form.validate().catch(() => false)
      if (!valid) return

      this.submitting = true
      const formData = new FormData()

      // 文本字段（空值不发送，交由后端默认值处理）
      const textFields = [
        'name', 'model', 'factory_no', 'department', 'manufacturer',
        'production_date', 'install_date', 'warranty_expiry_date',
        'service_life', 'price', 'expire_date', 'remark', 'status',
      ]
      textFields.forEach((key) => {
        const val = this.form[key]
        if (val !== undefined && val !== null && val !== '') {
          formData.append(key, val)
        }
      })

      // 布尔字段转为布尔后发送，与后端 _parse_bool 一致
      formData.append('is_fixed_asset', this.form.is_fixed_asset === '是')
      formData.append('has_certificate', this.form.has_certificate === '有')
      formData.append('has_registration', this.form.has_registration === '有')
      formData.append('has_production_license', this.form.has_production_license === '有')
      formData.append('has_business_license', this.form.has_business_license === '有')

      // 文件（固定文件名由后端处理）
      if (this.photoFile) formData.append('photo_file', this.photoFile)
      if (this.nameplateFile) formData.append('nameplate_file', this.nameplateFile)
      if (this.qualFile) formData.append('qualification_pdf', this.qualFile)

      try {
        const res = await equipmentApi.create(formData)
        this.qrcodeUrl = res.data.qrcode_url || ''
        this.saved = true
        this.$message.success('新增成功，二维码已生成')
        this.$emit('success')
      } catch (e) {
        const msg =
          e.response && e.response.data
            ? JSON.stringify(e.response.data)
            : '保存失败，请检查后端服务'
        this.$message.error(msg)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.section-title {
  font-size: 17px;
  font-weight: bold;
  text-align: center;
  color: #303133;
  margin: 20px 0 16px;
  position: relative;
}
.section-title::after {
  content: '';
  display: block;
  width: 48px;
  height: 3px;
  background: #409eff;
  border-radius: 2px;
  margin: 8px auto 0;
}
.upload-row {
  text-align: center;
  margin-bottom: 12px;
}
.upload-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 10px;
  text-align: center;
}
.image-box {
  width: 160px;
  height: 160px;
  margin: 0 auto;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.3s, border-style 0.3s;
  background-color: #fafafa;
}
.image-box:hover {
  border-color: #409eff;
}
/* 拖拽上传（2026-11-03）：el-upload 开了 drag 后会套一层 .el-upload-dragger，
   默认 360×180、带 padding 和虚线边框，会和 .image-box 叠成"双边框"。
   这里把外壳完全透明化，视觉只由 .image-box 承担。 */
.image-uploader /deep/ .el-upload-dragger {
  width: 160px;
  height: 160px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 6px;
  overflow: hidden;
}
/* 文件拖到框上方时点亮，明确提示"松手就传" */
.image-uploader /deep/ .el-upload-dragger.is-dragover .image-box {
  border-style: solid;
  border-color: #409eff;
  background-color: #ecf5ff;
}
.image-box.has-image {
  border-style: solid;
  border-color: #409eff;
  background-color: #fff;
}
.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.upload-placeholder {
  text-align: center;
  user-select: none;
}
.upload-icon {
  font-size: 28px;
  color: #8c939d;
  margin-bottom: 8px;
}
.upload-tip {
  font-size: 12px;
  color: #8c939d;
}
.image-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
  color: #fff;
  font-size: 24px;
}
.image-box:hover .image-mask {
  opacity: 1;
}
.image-mask .mask-icon {
  font-size: 26px;
  margin: 0 16px;
  cursor: pointer;
  transition: transform 0.2s;
}
.image-mask .mask-icon:hover {
  transform: scale(1.2);
}
.qr-success p {
  color: #67c23a;
  font-size: 12px;
  margin: 8px 0 0;
}
/* 统一所有输入控件宽度，确保普通 input、date-picker、select 右侧边框对齐 */
.equipment-form /deep/ .el-input,
.equipment-form /deep/ .el-date-editor,
.equipment-form /deep/ .el-select {
  width: 100% !important;
  display: block;
}
/* 日期选择器内部 input 也强制撑满 */
.equipment-form /deep/ .el-date-editor .el-input__inner {
  width: 100% !important;
}
/* 隐藏 date-picker 默认日历后缀图标，避免右侧多出空间 */
.equipment-form /deep/ .el-input__suffix-inner .el-icon-date {
  display: none;
}
/* 带 append（如使用期限“年”）的输入框恢复默认内边距 */
.equipment-form /deep/ .el-input-group--append .el-input__inner {
  padding-right: 15px;
}
/* 铭牌 OCR 识别回填的字段：黄色底色提示需人工核对 */
.ocr-filled /deep/ .el-input__inner {
  background-color: #fdf6ec;
}
/* 识别按钮下方提示 */
.ocr-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  text-align: center;
}
/* 自绘预览悬浮窗：可拖动、点击窗外穿透 */
.preview-float {
  position: fixed;
  z-index: 3000;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}
.preview-float-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  cursor: move;
  user-select: none;
}
.preview-float-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.preview-float-close {
  cursor: pointer;
  font-size: 18px;
  color: #909399;
}
.preview-float-close:hover {
  color: #409eff;
}
.preview-float-body {
  height: 65vh;
  overflow: hidden;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-float-tip {
  padding: 6px 0;
  text-align: center;
  font-size: 12px;
  color: #909399;
  border-top: 1px solid #ebeef5;
}
</style>
